import Box from "@mui/material/Box";
import DialogContent from "@mui/material/DialogContent";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";

import { IconButton } from "@covertree/ui/buttons/IconButton";
import { StatusLabel } from "@covertree/ui/content/StatusLabel";
import { LoadingOrValue } from "@covertree/ui/content/LoadingOrValue";
import { formatCurrency } from "@covertree/utils";

import {
  RenewalBaseFragment,
  RenewalWithCheckReceivalFragment,
} from "@covertree/agent-portal-graphql";

import { mapRenewalStatus } from "@utils/mapRenewalStatus";
import { formatProductTypeName } from "@utils/formatPolicyType";

import { ActionMenu } from "./ActionMenu";
import { mapRenewalStatusLabel } from "./utils/mapRenewalStatusLabel";
import { ContentTabs } from "./ContentTabs";

interface Props {
  onClose(): void;
  renewal?: RenewalBaseFragment | RenewalWithCheckReceivalFragment;
  loading: boolean;
  error: boolean;
}

export function RenewalDetailsContent({
  renewal,
  loading,
  error,
  onClose,
}: Props) {
  return (
    <>
      <ActionMenu loading={loading} renewal={renewal} />
      <Box
        sx={(theme) => ({
          position: "absolute",
          top: theme.spacing(2),
          right: theme.spacing(2),
        })}>
        <IconButton onClick={onClose} disabled={!renewal && loading}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Box py={2} px={3} mr={11}>
        <Box mb={2}>
          <Grid container spacing={2}>
            <Grid item>
              <StatusLabel
                status={mapRenewalStatusLabel(renewal?.status)}
                statusText={mapRenewalStatus(renewal?.status)}
                loading={!renewal && loading}
                error={error}
              />
            </Grid>
            <Grid item>
              <Typography variant="subtitle1" gutterBottom align="right">
                <LoadingOrValue
                  value={formatProductTypeName(renewal?.productTypeName)}
                  loading={!renewal && loading}
                  error={error}
                  height={20}
                  width={120}
                />
              </Typography>
            </Grid>
            <Grid item>
              <Typography variant="subtitle1" gutterBottom>
                &bull;
              </Typography>
            </Grid>
            <Grid item>
              <Typography variant="subtitle1" gutterBottom>
                <LoadingOrValue
                  value={renewal?.selectedPlan}
                  loading={!renewal && loading}
                  error={error}
                  height={20}
                  width={50}
                />
              </Typography>
            </Grid>
          </Grid>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={8}>
            <Typography variant="h3">
              <LoadingOrValue
                value={renewal?.renewalLocator}
                prefix="Renewal #"
                loading={!renewal && loading}
                error={error}
                height={40}
                width="100%"
              />
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="h3" color="secondary">
              <LoadingOrValue
                value={formatCurrency(renewal?.premium)}
                loading={!renewal && loading}
                error={error}
                height={40}
                width="100%"
              />
            </Typography>
          </Grid>
        </Grid>
      </Box>
      <DialogContent>
        <ContentTabs renewal={renewal} loading={loading} error={!!error} />
      </DialogContent>
    </>
  );
}
